import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NetworkControllerService} from '@netfoundry-ui/shared/apiv2';
import { NetworkV2, NetworkController } from '@netfoundry-ui/shared/model';
import { ApiService } from '@netfoundry-ui/shared/services';
import _ from 'lodash';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import { PagedGetOption } from '@lagoshny/ngx-hateoas-client';
import semver from 'semver';

const DEFAULT_SUBTITLE = "We'll finish creating your network once these DNS records exist.";
const MIGRATING_SUBTITLE = "We'll continue your migration to V8 once these DNS records exist.";
const SUPPORT_MIGRATING_SUBTITLE = "Have the customer create these DNS records before starting the migration to V8, to minimize downtime."

// The title reads "DNS Records Needed to Continue {{action}}".
const DEFAULT_ACTION = "Network Creation";
const MIGRATING_ACTION = "Network Migration"
const CONTROLLER_ACTION = "Controller Creation";
const CONTROLLER_SUBTITLE = "We'll finish setting up your network controller once these DNS records exist.";

const DEFAULT_BUTTON_LABEL = "Done";
const MIGRATING_BUTTON_LABEL = "Start Migration";

const DEFAULT_HEADER = "Records to create in your DNS"
const MIGRATING_HEADER = "Records for the customer to create in their DNS"

@Component({
    selector: 'lib-network-controller-info',
    templateUrl: './network-controller-info.component.html',
    styleUrls: ['./network-controller-info.component.scss'],
    standalone: false
})
export class NetworkControllerInfoComponent implements OnInit {
    networkModel: NetworkV2 = new NetworkV2();
    items: any = [];

    majorVersionNumber;
    currentNetwork;
    networkControllersList: NetworkController[] = [];
    isMigrating = false;
    subtitle = DEFAULT_SUBTITLE;
    action = DEFAULT_ACTION;
    buttonLabel = DEFAULT_BUTTON_LABEL;
    header = DEFAULT_HEADER;
    showCancelButton = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<NetworkControllerInfoComponent>,
        private apiService: ApiService,
        private networkControllerService: NetworkControllerService,
        public growlerService: GrowlerService,
    ) {
        this.isMigrating = data.isMigrating;
        this.networkModel = data.network;
        this.majorVersionNumber = apiService.getNetworkVersion(this.networkModel);

        if(this.isMigrating) {
            this.action = MIGRATING_ACTION;
            if(data.isSupport) {
                this.subtitle = SUPPORT_MIGRATING_SUBTITLE;
                this.buttonLabel = MIGRATING_BUTTON_LABEL
                this.header = MIGRATING_HEADER;
                this.showCancelButton = true;
            } else {
                this.subtitle = MIGRATING_SUBTITLE;
            }
        } else if(data.isController) {
            this.action = CONTROLLER_ACTION;
            this.subtitle = CONTROLLER_SUBTITLE;
        }
    }

    ngOnInit(): void {

        this.networkControllerService.getNetworkControllerPage(this.getOptions()).then(
            (result) => {
                this.networkControllersList = result;
                for (const controller of this.networkControllersList) {
                    // 🟢 Each controller gets new arrays
                    
                    if (!_.isEmpty(controller.domainName)) {

                        const domainName: string = controller.domainName;

                        if (!_.isEmpty(controller?.alternateDomainName)) {
                            this.items.push({
                                type: "CNAME",
                                recordName: controller.alternateDomainName,
                                value: domainName
                            });
                        }

                        if(!_.isEmpty(controller?.alternateDomainName) && this.isMigrating) {

                            const alternatePublicDomainNameSplit = controller.alternateDomainName.split(".");
                            const alternatePublicDomainName = `${alternatePublicDomainNameSplit.shift()}-p.${alternatePublicDomainNameSplit.join(".")}`;

                            this.items.push({
                                type: "CNAME",
                                recordName: alternatePublicDomainName,
                                value: domainName
                            });

                            const acmeChallengeValueSplit = domainName.split(".");
                            this.items.push({
                                type: "CNAME",
                                recordName: `_acme-challenge.${alternatePublicDomainName}`,
                                value: `_acme-challenge.custom.${acmeChallengeValueSplit.shift()}-p.${acmeChallengeValueSplit.join(".")}`
                            });


                        } else if(!_.isEmpty(controller.alternatePublicDomainName)) {

                            this.items.push({
                                type: "CNAME",
                                recordName: controller.alternatePublicDomainName,
                                value: domainName
                            });

                            const acmeChallengeValueSplit = domainName.split(".");
                            this.items.push({
                                type: "CNAME",
                                recordName: `_acme-challenge.${controller.alternatePublicDomainName}`,
                                value: `_acme-challenge.custom.${acmeChallengeValueSplit.shift()}-p.${acmeChallengeValueSplit.join(".")}`
                            });
                        }

                    }
                }
            }
        )
    }


    close(result: boolean) {
        this.dialogRef.close(result);
    }

    clickToCopy(value) {
        navigator.clipboard.writeText(value);
        this.growlerService.show(
            new GrowlerData('success', 'Success', 'Info Copied', `${value} has been copied to your clipboard`)
        );
    }


    csv_export() {
        if (!this.items || this.items.length === 0) {
            console.warn('No data to export.');
            return;
        }

        const data = [];
        const columns = [];

        // Build header from keys
        const header = {};
        for (const column of Object.keys(this.items[0])) {
            header[column] = column.toUpperCase();
            columns.push(column);
        }
        data.push(header);

        // Construct each row
        for (const item of this.items) {
            const row = {};

            for (const column of columns) {
            if (item[column] === undefined) {
                row[column] = '';
            } else {
                switch (column) {
                case 'type':
                    row[column] = item.type;
                    break;

                default:
                    row[column] = item[column];
                }
            }
            }

            data.push(row);
        }

        const options = {
            fieldSeparator: ',',
            quoteStrings: '"',
            decimalseparator: '.',
            showLabels: true,
            showTitle: false,
            useBom: true,
        };

        new AngularCsv(data, `Network_Custom_DNS_Entries_List`, options);
    }

    private getOptions() {
        const options: PagedGetOption = {
            params: { 
                networkId: this.networkModel.id,
                embed: 'all',
             },

        };
        return options;
    }
}

